/**
 * Voyage Home — Sprint 4 · Slice A · traveler-facing landing dashboard.
 *
 * Consumes: GET /api/traveler-context/me/current (Sprint 3 · Slice 1.2).
 *
 * Renders per ADR-TIME-001 · ADR-006 · ADR-REGION-001:
 *   · Current PHASE (PRE_CRUISE → POST_CRUISE) as the hero context
 *   · Three-clock strip (Ship · Port Local · Home) with authoritative_time badge
 *   · Next-event Ship-Time countdown
 *   · Phase-driven recommendations
 *   · Ports of the voyage (current + next)
 *
 * Empty state:
 *   · Users without `active_voyage_id` see a friendly "No active voyage"
 *     card with a link to the voyage picker.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useCruise } from "@/context/CruiseContext";
import ExploreShipModule from "@/components/ship-reference/ExploreShipModule";
import { api } from "@/lib/api";
import GeoVoyageMap from "@/components/shared/GeoVoyageMap";
import { Home, Ship, MapPin, Clock, Sparkles, ArrowRight, Loader2 } from "lucide-react";
// UXP-001 §17-A Dashboard Configuration Boundary (ratified 2026-08-12) +
// DES-FAMILY-SHARING-EXPERIENCE v1.2 · Family Sharing is a CONFIGURATION
// surface and now lives inside Edit Cruise → Family Sharing. Cruise Home
// remains operational intelligence only; the previous FamilySharingPanel
// mount has been retired. A future read-only "Shared with N" indicator
// (deep-linking into Edit Cruise → Family Sharing) may return here as a
// separate change per §17-A rule 2. Do not re-import the panel here.
// PortFocus retired from Dashboard 2026-02 (RC7-DASHBOARD-031). Component
// file preserved for other contexts; intentionally not imported here.
import ShipSchedule from "@/components/cruise-home/ShipSchedule";
import GuaranteeAttentionChip from "@/components/guarantees/GuaranteeAttentionChip";
// RC7-DASH-CLEANUP-001 · Booking Guardian retired from active dashboard
// (PO 2026-08-14). Historical governance untouched; component file kept.
import WorkspaceHero from "@/components/shared/WorkspaceHero";
import { toWorkspaceHeroProps } from "@/components/hero";
// INT-001 · Cruise Intelligence MVP · Weekly Briefing card
// (PO directive 2026-08-07 · GOV-DASH-001 exception granted)
import WeeklyBriefingCard from "@/components/cruise-home/WeeklyBriefingCard";

const API = process.env.REACT_APP_BACKEND_URL;

const PHASE_STYLES = {
  PRE_CRUISE:     { label: "Pre-cruise",     dot: "bg-sky-400",     ring: "ring-sky-200",     tint: "from-sky-50 to-white" },
  EMBARKATION:    { label: "Embarkation",    dot: "bg-amber-400",   ring: "ring-amber-200",   tint: "from-amber-50 to-white" },
  IN_PORT:        { label: "In port",        dot: "bg-emerald-400", ring: "ring-emerald-200", tint: "from-emerald-50 to-white" },
  AT_SEA:         { label: "At sea",         dot: "bg-blue-400",    ring: "ring-blue-200",    tint: "from-blue-50 to-white" },
  DISEMBARKATION: { label: "Disembarkation", dot: "bg-rose-400",    ring: "ring-rose-200",    tint: "from-rose-50 to-white" },
  POST_CRUISE:    { label: "Post-cruise",    dot: "bg-slate-400",   ring: "ring-slate-200",   tint: "from-slate-50 to-white" },
};

function fmtHHMM(iso, tz) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    // ADR-TIME-001: each clock must render in its own IANA timezone.
    // Without an explicit `timeZone` opt, `toLocaleTimeString` uses the
    // browser's local zone — collapsing Ship/Home/Port to the same wall
    // clock. Pass the projection's IANA name so Central and Eastern
    // (and any other pair) stay visually distinct.
    return d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: tz || undefined,
    });
  } catch { return "—"; }
}

// ---------------------------------------------------------------------
// Phase-driven headline composer (2026-02-17 · UI Regression #2)
// ---------------------------------------------------------------------
// Voyage Home must answer "Where am I in my journey?" within three
// seconds of opening the app. The hero's headline is context-driven —
// the AI-generated image is background support, never the message.
// ---------------------------------------------------------------------

function _addDays(isoDate, days) {
  if (!isoDate) return null;
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function _daysBetween(from, to) {
  if (!from || !to) return null;
  const a = new Date(`${from}T00:00:00Z`).getTime();
  const b = new Date(`${to}T00:00:00Z`).getTime();
  return Math.round((b - a) / 86400000);
}

function _humanDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" });
  } catch { return iso; }
}

function _dateRangeShort(fromIso, toIso) {
  if (!fromIso || !toIso) return null;
  try {
    const a = new Date(`${fromIso}T00:00:00Z`);
    const b = new Date(`${toIso}T00:00:00Z`);
    const opts = { day: "numeric", month: "short", timeZone: "UTC" };
    const yr = b.getUTCFullYear();
    return `${a.toLocaleDateString("en-GB", opts)} – ${b.toLocaleDateString("en-GB", opts)} ${yr}`;
  } catch { return null; }
}

function _totalVoyageDays(embarkIso, disembarkIso) {
  const d = _daysBetween(embarkIso, disembarkIso);
  return d === null ? null : d + 1;
}

function composeHero(phase, voyage, signals, events, todayIso) {
  const code = phase?.code || "PRE_CRUISE";
  const embarkPortName = phase?.embark_port?.name;
  const disembarkPortName = phase?.disembark_port?.name;
  const currentPortName = phase?.current_port?.name;
  const nextPortName = phase?.next_port?.name;
  const dayN = phase?.day_number;
  const daysToEmbark = signals?.days_to_embark;
  const embark = voyage?.embark_date;
  const disembark = voyage?.disembark_date;
  const totalDays = _totalVoyageDays(embark, disembark);

  // Voyage identity — always populated so every phase can render the
  // "what cruise · when · where it begins" block right under the
  // dominant headline. The traveler should never need to hunt for
  // this on Voyage Home.
  const identity = {
    shipName: voyage?.name || null,
    dateRange: _dateRangeShort(embark, disembark),
    departure: embarkPortName ? `Departing ${embarkPortName}` : null,
  };

  // Next-milestone helper — never returns "next port" during pre-cruise.
  const _milestone = () => {
    // Check-in typically opens ~30 days before sailing (industry norm).
    if (code === "PRE_CRUISE" && embark) {
      const checkinIso = _addDays(embark, -30);
      const daysToCheckin = _daysBetween(todayIso, checkinIso);
      if (daysToCheckin !== null && daysToCheckin > 0) {
        return { label: "Check-in opens", value: _humanDate(checkinIso), tail: `in ${daysToCheckin} day${daysToCheckin === 1 ? "" : "s"}` };
      }
      if (daysToEmbark !== null && daysToEmbark !== undefined) {
        if (daysToEmbark <= 1) return { label: "Boarding day", value: _humanDate(embark), tail: "today" };
        return { label: "Boarding day", value: _humanDate(embark), tail: `in ${daysToEmbark} day${daysToEmbark === 1 ? "" : "s"}` };
      }
    }
    if (code !== "PRE_CRUISE" && events?.length) {
      // In-voyage milestone = next Ship-Time event.
      const e = events[0];
      return { label: "Next in ship time", value: e.title, tail: `in ${e.in_label}` };
    }
    return null;
  };

  switch (code) {
    case "PRE_CRUISE": {
      const d = daysToEmbark;
      const dLabel = d === 0 ? "TODAY" : `${d ?? "—"} DAY${d === 1 ? "" : "S"}`;
      return {
        eyebrow: null,
        primary: dLabel,
        primarySize: "hero",
        secondary: "Until boarding",
        identity,
        milestone: _milestone(),
      };
    }
    case "EMBARKATION":
      return {
        eyebrow: null,
        primary: "TODAY",
        primarySize: "hero",
        secondary: "Welcome aboard",
        identity,
        milestone: _milestone(),
      };
    case "AT_SEA":
      return {
        eyebrow: nextPortName ? `Next port · ${nextPortName}` : null,
        primary: dayN && totalDays ? `DAY ${dayN} OF ${totalDays}` : (dayN ? `DAY ${dayN}` : "AT SEA"),
        primarySize: "hero",
        secondary: "Enjoy your sea day",
        identity,
        milestone: _milestone(),
      };
    case "IN_PORT":
      return {
        eyebrow: "NOW IN",
        primary: currentPortName || "In port",
        primarySize: "hero",
        secondary: dayN && totalDays ? `Day ${dayN} of ${totalDays}` : "In port today",
        identity,
        milestone: _milestone(),
      };
    case "DISEMBARKATION":
      return {
        eyebrow: disembarkPortName ? `Arriving · ${disembarkPortName}` : null,
        primary: "FINAL MORNING",
        primarySize: "hero",
        secondary: "Thank you for sailing",
        identity,
        milestone: _milestone(),
      };
    case "POST_CRUISE":
    default:
      return {
        eyebrow: "Post-cruise",
        primary: "WELCOME HOME",
        primarySize: "hero",
        secondary: "Memories from the voyage",
        identity,
        milestone: null,
      };
  }
}

function _tzAbbrev(iso, tz) {
  // Extract a short timezone abbreviation (e.g. CDT, EDT, GMT) using
  // Intl.DateTimeFormat with `timeZoneName: "short"`. Falls back to
  // the IANA identifier tail when short names aren't available.
  if (!tz) return null;
  try {
    const d = iso ? new Date(iso) : new Date();
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "short" }).formatToParts(d);
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    return tzPart?.value || null;
  } catch {
    return null;
  }
}

function _fmtDateStack(iso, tz) {  // eslint-disable-line no-unused-vars
  // RC7-DASHBOARD-025 · Retained for future date-stack use.
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const opts = { timeZone: tz || undefined };
    const month = d.toLocaleDateString("en-US", { ...opts, month: "short" }).toUpperCase();
    const day = d.toLocaleDateString("en-US", { ...opts, day: "numeric" });
    const weekday = d.toLocaleDateString("en-US", { ...opts, weekday: "short" }).toUpperCase();
    return { month, day, weekday };
  } catch {
    return null;
  }
}


// RC7-DASHBOARD-025 · Flat EventRow / date-stack presentation retired.
// The Ship Schedule is now port-organized (`ShipSchedule.jsx`), so the
// per-event row helpers, editorial `PORT_ENRICHMENT` map, and
// `_enrichPortName` helper have been removed with the flat view.
// Rationale: dead code violates the "no unused helpers" guideline and
// invites accidental resurrection of the retired flat schedule.

function ClockCard({ label, tz, sublabel, iso, isAuthoritative, icon, testId }) {
  // Product Owner directive (2026-02-17 · refinement 2):
  // Clock cards render label + wall-clock time + a small timezone pill
  // in the lower-right corner (e.g. CDT · EDT). The pill is subtle —
  // it must never compete with the time itself.
  //
  // 2026-02-21 refinement · The AUTHORITATIVE marker is a brand
  // element, not a generic status pill: Cruise Command Navy dot +
  // small navy label integrated INTO the header row, at the same
  // typographic scale as the label itself. The card border also
  // uses Navy (#071e3e) when authoritative so the brand element is
  // consistent from border to marker. The time itself remains the
  // dominant element on the card.
  const tzShort = _tzAbbrev(iso, tz);
  const NAVY = "#071e3e";
  return (
    <div
      className="relative flex-1 min-w-[130px] px-3 py-2.5 rounded-lg border bg-white"
      style={
        isAuthoritative
          ? { borderColor: NAVY, boxShadow: "0 1px 2px rgba(7, 30, 62, 0.08)" }
          : { borderColor: "#E2E8F0" }
      }
      data-testid={testId}
      data-tz-iana={tz || ""}
    >
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-500">
        {icon}
        <span className="min-w-0 truncate">{label}</span>
      </div>
      <div className="mt-1 text-2xl font-semibold text-slate-900 tabular-nums">
        {fmtHHMM(iso, tz)}
      </div>
      {sublabel && (
        <div className="text-[11px] text-slate-500 truncate">
          {sublabel}
        </div>
      )}
      {isAuthoritative && (
        // Refined metadata caption · Title Case, tight tracking, sits
        // beneath the time as supporting information — never a badge.
        // Navy dot + Title Case label = restrained brand element that
        // reads as "this is the source of truth" without shouting.
        <div
          className="mt-1 flex items-center gap-1.5 text-[11px] font-medium"
          style={{ color: NAVY, letterSpacing: "0.01em" }}
          data-testid={testId ? `${testId}-authoritative-marker` : undefined}
        >
          <span
            className="inline-block w-1.5 h-1.5 rounded-full"
            style={{ background: NAVY }}
            aria-hidden="true"
          />
          <span>Authoritative</span>
        </div>
      )}
      {tzShort && (
        <span
          className="absolute bottom-2 right-2.5 text-[9.5px] font-semibold tracking-[0.08em] text-slate-400"
          data-testid={testId ? `${testId}-tz` : undefined}
        >
          {tzShort}
        </span>
      )}
    </div>
  );
}

function RouteSnapshot({ nextPortId = null }) {
  // Route Snapshot — a compact nautical-chart map of the current voyage.
  // Sits beside the Upcoming itinerary and gives the traveler spatial
  // context (the shape of the voyage) before scanning event-by-event.
  //
  // Reuses the shared `GeoVoyageMap` (Leaflet + ESRI World Ocean Base)
  // so the visual language stays consistent with the Voyage workspace
  // map. Non-interactive by default — this is an editorial preview,
  // not a working surface. The full interactive map lives on the
  // Voyage workspace.
  //
  // RC7-DASHBOARD-036 · Optional `nextPortId` prop drives a subtle pulse
  // on the matching port pin so the traveller can visually tie the
  // map marker to the "Next port" chip on the Ship Schedule below.
  const { setRoute, portCalls } = useCruise();

  const routeSummary = (portCalls || [])
    .map((pc) => pc.port?.name)
    .filter(Boolean)
    .filter((n, i, arr) => n !== arr[i - 1])
    .join(" → ");

  return (
    <div data-testid="voyage-home-route-snapshot">
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 mb-3 flex items-center gap-1.5">
        <MapPin className="w-3 h-3" /> Route Map
      </div>
      <aside className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={() => setRoute("voyage")}
          data-testid="voyage-home-route-snapshot-open"
          className="block w-full text-left group focus:outline-none"
          aria-label="Open full voyage map"
        >
          <div className="rounded-none overflow-hidden pointer-events-none">
            <GeoVoyageMap
              variant="voyage"
              height={340}
              showShip={false}
              showLabels={false}
              fitPadding={[18, 18]}
              interactive={false}
              nextPortId={nextPortId}
            />
          </div>
          {routeSummary && (
            <div className="px-4 py-3 font-body text-[12px] text-slate-600 leading-relaxed">
              {routeSummary}
            </div>
          )}
        </button>
      </aside>
    </div>
  );
}

const CruiseHome = () => {
  const { setRoute, portCalls, selectedCruiseId, voyageMeta } = useCruise();
  const [state, setState] = useState({ loading: true });
  const [hero, setHero] = useState(null);

  // CKB-040 v4.1 · LAW-005 · re-hydrate every Dashboard data source
  // when `selectedCruiseId` changes. The axios `api` instance
  // auto-injects `?voyage_id=<selectedCruiseId>` on
  // `/api/traveler-context/me/current` via the shared interceptor,
  // so this hook depends on `selectedCruiseId` and refetches the
  // moment the Cruise Selector changes context. No workspace state
  // is cached across selections.
  useEffect(() => {
    let alive = true;
    setState({ loading: true });
    setHero(null);
    (async () => {
      try {
        const r = await api.get("/api/traveler-context/me/current");
        if (alive) setState({ loading: false, data: r.data });
      } catch (e) {
        const status = e?.response?.status;
        if (status === 404) {
          if (alive) setState({ loading: false, noVoyage: true });
          return;
        }
        if (alive) setState({ loading: false, error: String(e?.message || e) });
      }
    })();
    return () => { alive = false; };
  }, [selectedCruiseId]);

  // Ship hero — resolved via the Canonical Hero Resolver so any hero
  // change in CKB Studio propagates here automatically. The hero is
  // the primary visual element of Voyage Home per the 2026-02-17
  // design directive: "the editorial hero establishes emotional
  // context immediately upon opening the app."
  const shipId = state.data?.voyage?.ship_id;
  useEffect(() => {
    if (!shipId) return;
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/media/hero/ship/${shipId}`,
                              { credentials: "include" });
        if (!r.ok) return;
        const data = await r.json();
        if (alive) setHero(data);
      } catch { /* hero is decorative — never blocks the dashboard */ }
    })();
    return () => { alive = false; };
  }, [shipId]);

  // RC7-DASHBOARD-025 · The per-row weather chip now lives inside
  // ShipSchedule which reads coords directly off the enriched
  // itinerary port summary. No `portById` memo is required here.

  if (state.loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="voyage-home-loading">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="max-w-2xl mx-auto mt-16 p-6 rounded-lg border border-rose-200 bg-rose-50 text-rose-800" data-testid="voyage-home-error">
        <div className="text-sm font-semibold">Could not load Voyage Home</div>
        <div className="text-xs mt-1">{state.error}</div>
      </div>
    );
  }
  if (state.noVoyage) {
    return (
      <div className="max-w-2xl mx-auto mt-16" data-testid="voyage-home-empty">
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-slate-900 text-white flex items-center justify-center">
              <Ship className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xl font-semibold text-slate-900">No active voyage</div>
              <div className="text-sm text-slate-600">
                Pick a voyage to see your Voyage Home — phase, schedule, and traveler intelligence.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setRoute("voyages")}
            data-testid="voyage-home-empty-cta"
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
          >
            Open the voyage picker <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  const { data } = state;
  const phase = data.phase || {};
  const time = data.time || {};
  const signals = data.signals || {};
  const events = data.events || [];
  // RC7-DASH-CLEANUP-001 · "What matters now" recommendations retired
  // from the active dashboard (PO 2026-08-14). Backend payload keeps
  // shipping `recommendations`; the field is intentionally unread.

  // Default to metric °C — the Open-Meteo response is small and can be
  // re-fetched cheaply if the traveler flips units later.
  const weatherUnit = "metric";

  const cruiseName = data.voyage?.name || "Your cruise";
  const shipName = data.voyage?.ship_name || cruiseName;
  const dateRange = _dateRangeShort(data.voyage?.embark_date, data.voyage?.disembark_date);
  // Voyage label — "October 2026" style, derived from the embark date so
  // the traveler sees a distinct voyage identifier beneath the ship name
  // (rather than the departure month feeling appended to it).
  const voyageLabel = (() => {
    const iso = data.voyage?.embark_date;
    if (!iso) return null;
    try {
      const d = new Date(`${iso}T00:00:00Z`);
      return d.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
    } catch { return null; }
  })();

  // ── Phase-aware hero content (2026-02-17 · P1) ──
  // Every phase renders either a big countdown (pre-cruise emphasis)
  // or a single-line context. The hero should continue answering
  // "where am I in my journey?" as the traveler moves through the
  // voyage. Data comes from the traveler-context BFF payload.
  const heroCountdown = (() => {
    if (phase.code !== "PRE_CRUISE") return null;
    const d = signals?.days_to_embark;
    if (typeof d !== "number" || d <= 0) return null;
    return { value: d, unitLabel: d === 1 ? "day to sail" : "days to sail" };
  })();

  const heroContextLine = (() => {
    if (heroCountdown) return null;
    const code = phase.code;
    const totalDays = _totalVoyageDays(data.voyage?.embark_date, data.voyage?.disembark_date);
    const dayN = phase.day_number;

    switch (code) {
      case "EMBARKATION":
        return "Welcome aboard · Boarding today";
      case "AT_SEA": {
        const remaining = (totalDays && dayN) ? Math.max(0, totalDays - dayN) : null;
        if (dayN && totalDays) {
          const rem = remaining !== null ? ` · ${remaining} day${remaining === 1 ? "" : "s"} remaining` : "";
          return `Day ${dayN} of ${totalDays}${rem}`;
        }
        return "At sea today";
      }
      case "IN_PORT": {
        const portName = phase.current_port?.name;
        const mins = signals?.next_all_aboard_min;
        const allAboard = (typeof mins === "number" && mins > 0)
          ? ` · All aboard in ${mins < 60 ? `${mins} min` : `${Math.floor(mins / 60)}h ${mins % 60}m`}`
          : "";
        return portName ? `In ${portName}${allAboard}` : `In port today${allAboard}`;
      }
      case "DISEMBARKATION":
        return "Final morning · Journey home";
      case "POST_CRUISE":
        return "Welcome home";
      default:
        return null;
    }
  })();

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="voyage-home">
      {/* ── Editorial hero (shared WorkspaceHero, 2026-02-17) ────────
          Phase-aware content evolves automatically as the traveler
          moves through their voyage. Motion is a calm crossfade —
          purposeful, not decorative.

          RC · Hero Card Ecosystem (PO 2026-02-11): this call site
          now assembles a canonical `HeroData` object first, then
          hands it to `toWorkspaceHeroProps()`. Visual output is
          identical; the adapter is the future public entry point
          for every Operational Hero consumer. */}
      <WorkspaceHero
        testId="voyage-home-phase-card"
        {...toWorkspaceHeroProps({
          image: hero?.image_url || null,
          alt: hero?.alt || shipName || "",
          focal_point: hero?.focal_point,
          tint: hero?.environmental_mood,
          source_provenance: hero?.source,
          image_source_type: "ship",
          image_quality: hero?.quality,
          title: shipName,
          subtitle: [voyageLabel, dateRange].filter(Boolean).join(" · ") || null,
          meta: {
            phase: phase.code,
            countdown: heroCountdown
              ? { value: heroCountdown.value, unit_label: heroCountdown.unitLabel }
              : undefined,
            context_line: heroContextLine || undefined,
          },
          // RC7-DASHBOARD-027 · "Print Cruise Binder" hero action
          // removed on 2026-02-19. The Cruise Binder workspace,
          // routes (`setRoute("binder")`), components, and generation
          // pipeline remain intact and reachable through their own
          // dedicated navigation. The Dashboard hero is a live voyage
          // command surface, not a publishing surface.
        })}
      />

      {/* Family Sharing panel retired from Cruise Home on 2026-08-12
          per UXP-001 §17-A Dashboard Configuration Boundary and
          DES-FAMILY-SHARING-EXPERIENCE v1.2. Configuration workflow
          now lives at Edit Cruise → Family Sharing (last section of
          EditVoyageDrawer). Cruise Home may host a future read-only
          "Shared with N" indicator that deep-links to Edit Cruise —
          but never a configuration modal. */}

      {/* ── Three-clock strip (ADR-TIME-001) ─────────────────────────
          Presentation rules (2026-02-16):
            · Ship Time  — friendly tz name under the time, so the
                          traveler can distinguish clocks at a glance
                          when two projections share the same wall time.
            · Port Local — only rendered while phase === IN_PORT.
                          Sublabel = port name (falls back to friendly tz).
            · Home Time  — friendly tz name as sublabel.
          Backend continues to be the authority for every projection. */}
      {/* ── Traveler Time Intelligence (2026-02-17 · ADR-TIME-001 extension) ──
          Card 1  · Ship Time         — always present, authoritative operational time.
          Card 2  · Traveler Time    — always present, the traveler's personal timezone.
          Card 3  · Contextual Port   — driven purely by voyage phase:
                    PRE_CRUISE      → Departure port
                    EMBARKATION     → Port time
                    AT_SEA          → Next port
                    IN_PORT         → Port time
                    DISEMBARKATION  → Arrival port
                    POST_CRUISE     → Hidden
          Backend continues to be the authority for every projection. */}
      {(() => {
        // Resolve which port to feature in the contextual card.
        const p = phase.code;
        let port = null;
        let label = null;
        if (p === "PRE_CRUISE") {
          port = phase.embark_port; label = "Departure Port";
        } else if (p === "EMBARKATION") {
          port = phase.embark_port || phase.current_port; label = "Port Time";
        } else if (p === "AT_SEA") {
          port = phase.next_port; label = "Next Port";
        } else if (p === "IN_PORT") {
          port = phase.current_port; label = "Port Time";
        } else if (p === "DISEMBARKATION") {
          port = phase.disembark_port || phase.current_port; label = "Arrival Port";
        }
        const showContextCard = !!(port && port.timezone);
        return (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="voyage-home-clocks">
            <ClockCard
              label="Ship Time"
              tz={time.signals?.ship_timezone}
              iso={time.ship_time?.iso}
              isAuthoritative={time.authoritative_time === "ship"}
              icon={<Ship className="w-3 h-3" />}
              testId="voyage-home-clock-ship-time"
            />
            {time.home_time && (
              <ClockCard
                label="Traveler Time"
                tz={time.signals?.home_timezone}
                iso={time.home_time?.iso}
                isAuthoritative={time.authoritative_time === "home"}
                icon={<Home className="w-3 h-3" />}
                testId="voyage-home-clock-traveler-time"
              />
            )}
            {showContextCard && (
              <ClockCard
                label={label}
                tz={port.timezone}
                sublabel={port.name}
                iso={time.ship_time?.iso /* uses ship's ISO instant, formatted in the port's tz */}
                isAuthoritative={false}
                icon={<MapPin className="w-3 h-3" />}
                testId="voyage-home-clock-context"
              />
            )}
          </div>
        );
      })()}

      {/* CCD-DASH-012 · Adaptive Journey Card · PAUSED (PO 2026-08-05).
          Component ships correctly (six lifecycle variants, time-aware,
          contextual empty states) but the PO judged it doesn't provide
          enough value to occupy premium dashboard space. Journey
          remains a first-class workspace and should not be duplicated
          on the Dashboard.

          The card component file remains at
              /app/frontend/src/components/cruise-home/AdaptiveJourneyCard.jsx
          for reuse when the next dashboard design sprint decides what
          this location deserves (Voyage Intelligence · Cruise
          Readiness · Today's Priorities · Operational Alerts are the
          current candidate concepts). Until then: less is better. */}

      {/* INT-001 · Weekly Cruise Briefing card (PO 2026-08-07).
          GOV-DASH-001 exception granted by explicit PO directive.
          Placement: between the three-clock strip and the Route
          Snapshot / Upcoming grid, per PO decision. Card hides
          entirely (silent) when no published briefing exists —
          no placeholder messaging. Consumes
          GET /api/cruises/current/briefing/latest. */}
      <WeeklyBriefingCard />

      {/* ── ROUTE MAP · full-width band (RC7-DASHBOARD-030 · 2026-02-19)
          Retired the compact two-column layout. The Route Map now
          spans the full Dashboard content width so the voyage shape
          is legible without squinting. `RouteSnapshot` reuses the
          same GeoVoyageMap component and same route data — no map
          replacement, no route overlays, no coordinate changes. */}
      {portCalls.length > 0 && (
        <div className="mt-6">
          <RouteSnapshot nextPortId={phase?.next_port?.id || null} />
        </div>
      )}

      {/* ── SHIP SCHEDULE · full-width band (RC7-DASHBOARD-030)
          Stacks beneath the Route Map at full width; every port
          card now stretches to the container width so operational
          times and Explore action read comfortably. Ownership and
          all functionality unchanged. */}
      {portCalls.length > 0 && (
        <div className="mt-6">
          <ShipSchedule
            voyage={data.voyage}
            itinerary={portCalls}
            phase={phase}
            signals={signals}
            weatherUnit={weatherUnit}
            onOpenDestination={(subjectId) => setRoute(`destination-guide:${subjectId}`)}
          />
        </div>
      )}

      {/* ── In-Port Intelligence (Slice B) retired ──────────────────
          Removed 2026-02 (RC7-DASHBOARD-031, PO authorised).
          Ship Schedule is now the primary port navigation surface, so
          the large PortFocus hero below is redundant. PortFocus.jsx
          remains in the codebase (used elsewhere / for potential
          future contexts) but is no longer mounted on the Dashboard. */}

      {/* ── Recommendations ("What matters now") retired ──────────────
          Removed 2026-08-14 (RC7-DASH-CLEANUP-001, PO authorised).
          Backend `data.recommendations` still ships; the dashboard
          intentionally does not render it. No replacement filler card. */}

      {/* ── Time signals debug (below-the-fold, ADR-TIME-001) ────── */}
      {(signals.next_all_aboard_min != null || time.time_change_today) && (
        <div className="mt-6 text-[11px] text-slate-500 flex flex-wrap gap-x-4 gap-y-1" data-testid="voyage-home-signals">
          {signals.next_all_aboard_min != null && (
            <span>All aboard in <span className="font-semibold text-slate-700">{signals.next_all_aboard_min} min</span></span>
          )}
          {time.time_change_today && <span>⏰ Ship clock changes today</span>}
          {time.time_change_tomorrow && <span>⏰ Ship clock changes tomorrow</span>}
          {time.next_time_change && (
            <span>Next change: {time.next_time_change.direction} at {fmtHHMM(time.next_time_change.at_iso, time.signals?.ship_timezone)}</span>
          )}
        </div>
      )}
      {/* ── Booking Guardian retired from active dashboard ─────────
          Removed 2026-08-14 (RC7-DASH-CLEANUP-001, PO authorised).
          Component file preserved for historical governance; no
          replacement card. */}
      {/* RC7-IA-026 · Full Guarantee Ledger relocated to the Add-Ons
          workspace on 2026-02-19. Dashboard now carries only a
          compact actionable attention chip that deep-links back to
          the ledger. The chip self-hides when the ledger has no
          actionable state (outstanding + disputed == 0). */}
      {/* RC8 · Compact "Explore ship" module. The complete Widgety catalogue
          lives in the dedicated Ship Guide (opened via the CTA) — Voyage Home
          stays personalised and concise, never rendering the full lists. */}
      <div className="mt-6">
        <ExploreShipModule
          shipId={voyageMeta?.ship_id}
          shipName={voyageMeta?.ship_name || ""}
          onOpenGuide={(tab) => setRoute(tab ? `ship-guide:${tab}` : "ship-guide")}
        />
      </div>
      <GuaranteeAttentionChip />
    </div>
  );
};

export default CruiseHome;
